import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import Modal from '../shared/Modal'
import Banner from '../ui/Banner'
import Button from '../ui/Button'
import Checkbox from '../ui/Checkbox'
import ModalHeader from './ModalHeader'

const EMPTY_PREFERENCES = {}

const MODAL_TITLE = 'Personnaliser l\'export PDF'

const renderFormatRadio = ({ id, value, label, checked, disabled = false, onChange }) => (
  <div className="form-check">
    <input
      type="radio"
      className="form-check-input tw:accent-ds-neutral-highest"
      id={id}
      name="pdf-format"
      value={value}
      checked={checked}
      disabled={disabled}
      onChange={onChange}
    />
    <label className="form-check-label" htmlFor={id}>
      {label}
    </label>
  </div>
)

const renderInfoField = (label, value) => (
  <div className="tw:flex tw:flex-col tw:gap-1">
    <p className="tw:text-gray-500 tw:m-0!">{label}</p>
    <p className="tw:text-gray-900 tw:m-0!">{value}</p>
  </div>
)

const PdfOptionsModal = ({
  onClose, onExport, collect, startingAt, savedPreferences = EMPTY_PREFERENCES,
  singlePdfLimit
}) => {
  const [withVariables, setWithVariables] = useState(savedPreferences.all_variables ?? true)
  const [exportAsZip, setExportAsZip] = useState(
    Boolean(singlePdfLimit) || savedPreferences.export_as_zip !== false
  )
  const [submitting, setSubmitting] = useState(false)

  const ensemble = collect.ensembles.find(item => item.id === collect.ensemble_id)
  const collectLabel = startingAt ? `${collect.name} (planification du ${startingAt})` : collect.name

  const handleWithVariablesChange = useCallback(() => {
    setWithVariables(prev => !prev)
  }, [])

  const handleExportAsZipChange = useCallback(event => {
    setExportAsZip(event.target.value === 'true')
  }, [])

  const handleSubmit = useCallback(event => {
    event.preventDefault()
    if (submitting)
      return

    setSubmitting(true)
    onExport({ withVariables, exportAsZip })
  }, [onExport, withVariables, exportAsZip, submitting])

  return (
    <Modal
      title={MODAL_TITLE}
      header={<ModalHeader title={MODAL_TITLE} onClose={onClose} />}
      footer={
        <div className="tw:flex tw:items-center tw:justify-end tw:w-full tw:gap-3">
          <Button
            className="tw:bg-transparent! tw:border-0! tw:text-gray-900!"
            variant="neutral"
            onClick={onClose}
          >
            Annuler
          </Button>
          <Button disabled={submitting} variant="primary" onClick={handleSubmit}>
            {submitting ? 'Export en cours...' : 'Exporter'}
          </Button>
        </div>
      }
      isOpen
      onClose={onClose}
    >
      <div className="tw:flex tw:flex-col tw:gap-4">
        <div
          className={'tw:flex tw:flex-col tw:gap-3 tw:rounded-md! tw:border tw:border-solid '
            + 'tw:border-gray-300 tw:p-4'}
        >
          {renderInfoField('Collecte', collectLabel)}
          {renderInfoField('Ensemble', ensemble?.name)}
        </div>
        <div className="tw:flex tw:flex-col tw:gap-2">
          {renderFormatRadio({
            id: 'pdf-format-multiple',
            value: 'true',
            label: 'Plusieurs PDF (fichier ZIP)',
            checked: exportAsZip,
            onChange: handleExportAsZipChange
          })}
          {renderFormatRadio({
            id: 'pdf-format-single',
            value: 'false',
            label: 'PDF unique',
            checked: !exportAsZip,
            disabled: Boolean(singlePdfLimit),
            onChange: handleExportAsZipChange
          })}
          {singlePdfLimit ? <Banner variant="neutral">{singlePdfLimit}</Banner> : null}
        </div>
        <label className="tw:flex! tw:items-center tw:gap-2 tw:cursor-pointer tw:m-0!">
          <Checkbox
            id="export_variables"
            checked={withVariables}
            onChange={handleWithVariablesChange}
          />
          <span className="tw:text-gray-900">Inclure les variables des ressources</span>
        </label>
      </div>
    </Modal>
  )
}

PdfOptionsModal.propTypes = {
  collect: PropTypes.shape({
    ensembles: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired
      })
    ).isRequired,
    // eslint-disable-next-line camelcase
    ensemble_id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onExport: PropTypes.func.isRequired,
  singlePdfLimit: PropTypes.string
}

PdfOptionsModal.defaultProps = {
  singlePdfLimit: null
}

export default PdfOptionsModal
